<template>
    <template v-if="errorMessage" class="error-message">
        <p>{{ errorMessage }}</p>
    </template>

    <template v-else>
        <div class="calendar-list" role="region" aria-label="Events calendar list" aria-live="polite" aria-atomic="false">
            <div v-if="groupedEventsByMonth.length === 0" class="calendar-list__empty">
                <p>There are performances but you filtered them out</p>
            </div>

            <div v-else>
                <div class="calendar-list__status" aria-live="polite" aria-atomic="true">
                    <span class="visually-hidden">Showing {{ displayedEventCount }} of {{ totalEventCount }}
                        performances</span>
                </div>

                <button
                    v-if="featureEnabled('calendar.paginated') && hasPreviousMonthEvents()"
                    @click="goToPreviousMonth"
                    class="calendar__nav-button calendar__nav-button--prev"
                    aria-label="View Previous Events"
                >
                    View Previous Events
                </button>
                <ul class="calendar-list__months">
                    <li v-for="monthGroup in groupedEventsByMonth" :key="monthGroup.month" class="calendar-list-month"
                        :data-month="monthGroup.month">

                        <h2 class="calendar-list-month__title">{{ monthGroup.month }}</h2>

                        <ul class="calendar-list-month__days">
                            <li v-for="dayGroup in monthGroup.days" :key="dayGroup.dayKey" class="calendar-list-day"
                                :data-day="dayGroup.dayKey">

                                <h3 class="calendar-list-day__title">
                                    <span class="calendar-list-day__weekday">{{ dayGroup.day.slice(0, 3) }}</span>
                                    <span class="calendar-list-day__date">{{ dayGroup.date }}</span>
                                    <span class="calendar-list-day__month">{{ monthGroup.month.slice(0, 3)  }}</span>
                                </h3>

                                <ul class="calendar-list-events">
                                    <li v-for="event in dayGroup.events" :key="event.event.id" class="calendar-list-event-li">

                                        <button type="button" class="calendar-list-event"
                                                @click="selectedEvent = event"
                                                @keydown.enter="selectedEvent = event"
                                                @keydown.space="selectedEvent = event"
                                                :aria-label="event.event.title + ' on ' + formatDateDisplay(event.startDate) + ' at ' + formatTimeDisplay(event.startDate)"
                                        >
                                            <img v-if="event.event.image?.src" :src="event.event.image.src" :alt="event.event.title" class="calendar-list__event-image" />
                                            <div class="calendar-list-event__info">
                                                <div class="calendar-list-event__header">
                                                    <p v-if="event.prefix" class="calendar-list-event__prefix">{{ event.prefix }}</p>
                                                    <div v-if="event.title" class="calendar-list-event__title" v-html="event.title"></div>
                                                    <p v-if="event.suffix" class="calendar-list-event__suffix">{{ event.suffix }}</p>
                                                    <p
                                                        class="calendar-list-event__performance-subtitle"
                                                        v-if="event.attributes?.custom?.subtitle"
                                                    >{{ event.attributes.custom.subtitle }}</p>
                                                    <time class="calendar-list-event__datetime" :datetime="event.event.startDate">
                                                        <span class="calendar-list-event__date">{{formatDateDisplay(event.performancedate) }}</span>
                                                        <span class="calendar-list-event__time">{{formatTimeDisplay(event.performancedate) }}</span>
                                                    </time>
                                                    <span v-if="event.event.venue" class="calendar-list-event__venue">
                                                        {{ event.event.venue.title }}
                                                    </span>
                                                    <span v-if="event.event.attributes.custom?.repertory" class="calendar-list-event__repertory">
                                                        {{ event.event.attributes.custom.repertory }}
                                                    </span>
                                                </div>
                                                <div class="calendar-list-event__footer">
                                                    <ul v-if="event.event.attributes?.custom?.genres?.length" class="calendar-list-event__genres">
                                                        <li v-for="(genre, idx) in event.event.attributes.custom.genres"
                                                            :key="idx" class="calendar-list-event__genre-badge">
                                                            {{ genre }}
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                </ul>
                <button
                    v-if="featureEnabled('calendar.paginated') && hasNextMonthEvents()"
                    @click="goToNextMonth"
                    class="calendar__nav-button calendar__nav-button--next"
                    :aria-label="`View Following Events. Currently showing ${displayedEventCount} of ${totalEventCount}`"
                    :aria-busy="false"
                >
                    View Following Events
                </button>
            </div>
        </div>
    </template>
</template>

<script setup>
import {computed, ref, watch, nextTick} from 'vue';
import { startOfDay, endOfDay, startOfMonth, endOfMonth, addDays, subDays, addMonths, subMonths } from 'date-fns';
import { formatEventDate, formatEventTime, formatMonthKey, formatDayKey, formatDay, formatDate, formatOrdinal } from '../utils/date-formatters.mjs';
import {setState, useState} from '../services/stateful';
import {getFirstEventOfMonth} from "../utils/event.mjs";

const SCROLL_INTO_VIEW_CLASS = 'calendar-list-day--scroll-into-view';

const props = defineProps({
    events: {
        type: Array,
        required: true,
    },
});
const featureEnabled = window.featureEnabled; //Required for access in template

const activeMonthTails = ref(7); //How many days to show before & after currently selected month
const monthChanged = ref(false);

const selectedDate = useState('Calendar.SelectedDate', null);
const selectedEvent = useState('Calendar.SelectedEvent', null);
const errorMessage = useState('Calendar.ErrorMessage', null);

const formatDateDisplay = useState('CalendarList.Format.DateDisplay', formatEventDate);
const formatTimeDisplay = useState('CalendarList.Format.TimeDisplay', formatEventTime);
const scrollToDayKey = useState('CalendarList.ScrollToDayKey', null);
const activeMonth = useState('CalendarList.ActiveMonth', new Date());

const eventsPerPage = 25;
const displayedEventCount = ref(eventsPerPage);

const availableMonths = computed(() => {
    const months = new Map();

    props.events.forEach(event => {
        const date = event.startDate;
        if (isNaN(date.getTime())) {
            console.warn(`Invalid date in event: ${date}`);
            return;
        }
        const monthKey = formatMonthKey(date);
        const monthDate = new Date(date.getFullYear(), date.getMonth(), 1);

        if (!months.has(monthKey)) {
            months.set(monthKey, monthDate);
        }
    });

    return Array.from(months.values()).sort((a, b) => a - b);
});

const groupedEventsByMonth = computed(() => {
    let eventsToGroup = props.events;

    if (featureEnabled('calendar.paginated')) {
        const monthStart = startOfMonth(activeMonth.value);
        const monthEnd = endOfMonth(activeMonth.value);
        const rangeStart = subDays(monthStart, activeMonthTails.value);
        const rangeEnd = addDays(monthEnd, activeMonthTails.value);

        eventsToGroup = props.events.filter(event => {
            return event.startDate >= rangeStart && event.startDate <= rangeEnd;
        });
    }

    const grouped = {};
    const seenDates = new Set();

    eventsToGroup.forEach(event => {
        const eventDate = event.startDate;

        if (!seenDates.has(eventDate)) seenDates.add(eventDate);

        const monthKey = formatMonthKey(event.startDate);
        const dayKey = formatDayKey(event.startDate);

        if (!grouped[monthKey]) {
            grouped[monthKey] = {};
        }
        if (!grouped[monthKey][dayKey]) {
            grouped[monthKey][dayKey] = [];
        }
        grouped[monthKey][dayKey].push(event);
    });

    return Object.entries(grouped)
        .sort(([, daysA], [, daysB]) => {
            const daysAEntries = Object.values(daysA)[0];
            const daysBEntries = Object.values(daysB)[0];
            const dateA = daysAEntries[0].startDate;
            const dateB = daysBEntries[0].startDate;
            return dateA - dateB;
        })
        .map(([month, daysByKey]) => ({
            month,
            days: Object.entries(daysByKey)
                .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                .map(([dayKey, events]) => {
                    const sortedEvents = events.sort((a, b) => a.startDate - b.startDate);
                    return {
                        dayKey,
                        startDate: sortedEvents[0].startDate,
                        day: formatDay(sortedEvents[0].startDate),
                        ordinal: formatOrdinal(sortedEvents[0].startDate),
                        date: formatDate(sortedEvents[0].startDate),
                        events: sortedEvents
                    };
                })
        }));
});

const totalEventCount = computed(() => {
    return groupedEventsByMonth.value.reduce((sum, monthGroup) => {
        return sum + monthGroup.days.reduce((daySum, dayGroup) => daySum + dayGroup.events.length, 0);
    }, 0);
});

const announceToScreenReader = (message) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'visually-hidden';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
        document.body.removeChild(announcement);
    }, 5000);
};

const loadAllEvents = () => {
    displayedEventCount.value = totalEventCount.value;
};

const hasPreviousMonthEvents = () => {
    if (!featureEnabled('calendar.paginated')) {
        return false;
    }

    if (availableMonths.value.length === 0) {
        return false;
    }

    const currentActiveMonth = startOfMonth(activeMonth.value);
    const currentIndex = availableMonths.value.findIndex(month => {
        return month.getMonth() === currentActiveMonth.getMonth() &&
            month.getFullYear() === currentActiveMonth.getFullYear();
    });

    if (currentIndex <= 0) {
        return false;
    }

    const monthStart = startOfMonth(activeMonth.value);
    const rangeStart = subDays(monthStart, activeMonthTails.value);

    const previousMonth = subMonths(activeMonth.value, 1);
    const previousMonthStart = startOfMonth(previousMonth);
    const previousMonthEnd = endOfMonth(previousMonth);

    const previousMonthEvents = props.events.filter(event => {
        return event.startDate >= previousMonthStart && event.startDate <= previousMonthEnd;
    });

    if (previousMonthEvents.length === 0) {
        return false;
    }

    const allEventsInTail = previousMonthEvents.every(event => {
        return event.startDate >= rangeStart;
    });

    return !allEventsInTail;
};

const hasNextMonthEvents = () => {
    if (!featureEnabled('calendar.paginated')) {
        return false;
    }

    if (availableMonths.value.length === 0) {
        return false;
    }

    const currentActiveMonth = startOfMonth(activeMonth.value);
    const currentIndex = availableMonths.value.findIndex(month => {
        return month.getMonth() === currentActiveMonth.getMonth() &&
            month.getFullYear() === currentActiveMonth.getFullYear();
    });

    if (currentIndex === -1 || currentIndex >= availableMonths.value.length - 1) {
        return false;
    }

    const monthEnd = endOfMonth(activeMonth.value);
    const rangeEnd = addDays(monthEnd, activeMonthTails.value);

    const nextMonth = addMonths(activeMonth.value, 1);
    const nextMonthStart = startOfMonth(nextMonth);
    const nextMonthEnd = endOfMonth(nextMonth);

    const nextMonthEvents = props.events.filter(event => {
        return event.startDate >= nextMonthStart && event.startDate <= nextMonthEnd;
    });

    if (nextMonthEvents.length === 0) {
        return false;
    }

    const allEventsInTail = nextMonthEvents.every(event => {
        return event.startDate <= rangeEnd;
    });

    return !allEventsInTail;
};

const goToPreviousMonth = () => {
    activeMonth.value = subMonths(activeMonth.value, 1);
    const earliestEventDate = getFirstEventOfMonth(activeMonth.value, props.events);
    scrollToDayKey.value = formatDayKey(earliestEventDate);
};

const goToNextMonth = () => {
    activeMonth.value = addMonths(activeMonth.value, 1);
    const earliestEventDate = getFirstEventOfMonth(activeMonth.value, props.events);
    scrollToDayKey.value = formatDayKey(earliestEventDate);
};

const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            entry.target.classList.remove(SCROLL_INTO_VIEW_CLASS);
            observer.disconnect(entry);
        }
    });
}, {
    threshold: 0.5,
});

watch(() => scrollToDayKey.value, async (dayKey) => {
    if (!dayKey) return;

    setState('Calendar.Loading', false);
    await nextTick();

    const dayElement = document.querySelector(`[data-day="${dayKey}"]`);
    if (dayElement) {
        observer.observe(dayElement);
        dayElement.classList.add(SCROLL_INTO_VIEW_CLASS);
        dayElement.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    scrollToDayKey.value = null;
    monthChanged.value = false;
});

watch(() => selectedDate.value, async (date) => {
    if (!date) return;
    setState('Calendar.Loading', true);

    if (featureEnabled('calendar.paginated')) {
        const currentMonth = activeMonth.value.getMonth();
        const currentYear = activeMonth.value.getFullYear();
        const newMonth = date.getMonth();
        const newYear = date.getFullYear();

        if (currentMonth !== newMonth || currentYear !== newYear) {
            monthChanged.value = true;
            activeMonth.value = date;
        } else {
            activeMonth.value = date;
        }
    }

    const dayStart = startOfDay(date);
    const dayEnd = endOfDay(date);

    const eventOnDate = props.events.find(event => {
        return dayStart <= event.startDate && event.startDate <= dayEnd;
    });

    if (eventOnDate) {
        loadAllEvents();
    }

    scrollToDayKey.value = formatDayKey(selectedDate.value);
    selectedDate.value = null;
});
</script>
